import { useEffect, useState } from "react";
import {
  ArrayInput,
  AutocompleteInput,
  minValue,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useDataProvider,
} from "react-admin";
import { useWatch } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import { settings } from "app";
import { administrationRequests } from "api/administrationApi";
import { studentRequests } from "api/studentApi";
import { employeeRequests } from "api/employeeApi";
import { documentRequests } from "api/documentsApi";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import DocumentFileInput from "components/Erasmus/Partials/DocumentFileInput";
import { GradingTypeOptions } from "utils/choices";
import { validateEnd, validateStart } from "utils/validations";
import EligibilityIteratorItem from "./Partials/EligibilityIteratorItem";
import ProfessorsIteratorItem from "./Partials/ProfessorsIteratorItem";

const SaveForm = ({ onDocumentUpload }) => {
  const departmentId = useWatch({ name: "departmentId" });
  const dataProvider = useDataProvider();
  const [mfStructuralUnitId, setMfStructuralUnitId] = useState(null);

  const validateDurationFrom = (value, allValues) => validateStart(value, allValues.durationTo, true);
  const validateDurationTo = (value, allValues) => validateEnd(value, allValues.durationFrom, true);

  useEffect(() => {
    dataProvider
      .customRequest(administrationRequests.getStructuralUnitsSelect())
      .then(res => {
        const structuralUnits = res.data ?? [];
        const medicalFaculty = structuralUnits.find(
          structuralUnit => structuralUnit?.name?.toLowerCase() === "медицински факултет",
        );
        setMfStructuralUnitId(medicalFaculty?.id ?? null);
      })
      .catch(error => console.error(error));
  }, [dataProvider]);

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="nameBg"
          label="Име на модула (български)"
          validate={required()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <TextInput
          source="nameEn"
          label="Име на модула (английски)"
          validate={required()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="academicYearId"
          reference={studentRequests.getAcademicYearsSelect()}
        >
          <AutocompleteInput
            label="Академична година"
            optionText="name"
            fullWidth
            validate={required()}
          />
        </ReferenceInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="departmentId"
          reference={administrationRequests.getDepartmentsSelect()}
          filter={mfStructuralUnitId ? { StructuralUnitId: mfStructuralUnitId } : undefined}
        >
          <AutocompleteInput
            label="Катедра"
            optionText="fullName"
            fullWidth
            validate={required()}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
          />
        </ReferenceInput>
      </Grid>
      {departmentId ? (
        <Grid
          item
          xs={12}
          md={6}
        >
          <ReferenceInput
            source="departmentLeadEmployeeId"
            reference={employeeRequests.getDepartmentLeadsSelect(departmentId)}
          >
            <AutocompleteInput
              label="Ръководител катедра"
              optionText={getProfessorRecordText}
              fullWidth
              validate={required()}
              disabled={!departmentId}
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            />
          </ReferenceInput>
        </Grid>
      ) : null}

      <Grid
        item
        xs={12}
      >
        <Typography
          color="primary"
          sx={{ marginTop: 2 }}
        >
          За кого е достъпен
        </Typography>
        <ArrayInput
          source="electiveCourseSpecialities"
          label={false}
          defaultValue={[{}]}
        >
          <SimpleFormIterator
            inline={false}
            sx={settings.style.iteratorWithoutGap}
          >
            <EligibilityIteratorItem mfStructuralUnitId={mfStructuralUnitId} />
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>

      <Grid
        item
        xs={12}
      >
        <DocumentFileInput
          source="documentId"
          label="Програма на модула"
          uploadUrl={studentRequests.postElectiveCourseDocument()}
          downloadUrl={id => documentRequests.getDocumentById(id, "public")}
          fileOnly
          onUploadSuccess={data => onDocumentUpload?.(data?.id ?? data)}
          validate={required()}
        />
      </Grid>

      <Grid
        item
        xs={12}
      >
        <Typography
          color="primary"
          sx={{ marginTop: 2 }}
        >
          Преподаватели (незадължително)
        </Typography>
        <ArrayInput
          source="professors"
          label={false}
          defaultValue={[]}
        >
          <SimpleFormIterator
            inline={false}
            sx={settings.style.iteratorWithoutGap}
          >
            <ProfessorsIteratorItem />
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
      >
        <NumberInput
          source="horariumHoursLectures"
          label="Хорариум (лекции)"
          validate={required()}
          onWheel={e => e.target.blur()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <NumberInput
          source="horariumHoursExercises"
          label="Хорариум (упражнения)"
          validate={required()}
          onWheel={e => e.target.blur()}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <NumberInput
          source="credits"
          label="Кредити"
          validate={required()}
          onWheel={e => e.target.blur()}
          fullWidth
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="buildingId"
          reference={administrationRequests.getBuildingsSelect()}
        >
          <AutocompleteInput
            label="Място на провеждане (сграда)"
            optionText="name"
            fullWidth
            validate={required()}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
      >
        <SelectInput
          source="gradingType"
          label="Форма на оценяване"
          choices={GradingTypeOptions}
          validate={required()}
          fullWidth
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
      >
        <CustomDateInput
          source="durationFrom"
          label="Период от"
          validate={[required(), validateDurationFrom]}
          required
          noTime
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <CustomDateInput
          source="durationTo"
          label="Период до"
          validate={[required(), validateDurationTo]}
          required
          noTime
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <NumberInput
          source="studentCapacity"
          label="Капацитет"
          validate={[required(), minValue(1)]}
          onWheel={e => e.target.blur()}
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

export default SaveForm;
